import { useState, useEffect, createContext, lazy, Suspense, useRef } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";

import Modal from "../components/common/Modal";
import Header from "../components/common/Header";
import HeaderMessage from "../components/home/HeaderMessage";
import Banner from "../components/home/Banner";
import CardSkeleton from "../components/common/CardSkeleton";
import Loader from "../components/common/Loader";
import FilterSkeleton from "../components/common/FilterSkeleton";
import SideBarSkeleton from "../components/common/SideBarSkeleton";
import FormSkeleton from "../components/common/FormSkeleton";

import { getDegreeValue, getCategoryValue, getDegreeList, capitalizeFirstLetter, getCategoryList, getMergedRankedOffers, getStateFromCode, modifyMetaDescription, trackGtmPixel } from "../utils/common";
import { useWindowSize } from "../utils/hooks";
import { CATEGORY_LIST } from "../data";
import { fetchInitForm, fetchVisitData } from "../utils/common/api";

const SerpContainerLazy = lazy(() => import("../layouts/SerpContainer"));
const Form = lazy(() => import("../components/form/Form"));
const Results = lazy(() => import("../layouts/Results"));
const CollegEnrollUSP = lazy(() => import("../components/home/CollegEnrollUSP"));
const Article = lazy(() => import("../components/home/Article"));
const UniversityReviews = lazy(() => import("../components/home/UniversityReviews"));
const CollegeComparisonGuide = lazy(() => import("../components/home/CollegeComparisonGuide"));
const OurTopUniversity = lazy(() => import("../layouts/OurTopUniversity"));
const FaqSection = lazy(() => import("../components/home/FaqSection"));
const Footer = lazy(() => import("../layouts/Footer"));
const Error = lazy(() => import("../layouts/Error"));

const Home = () => {
    const [mainData, setMainData] = useState({});
    const [schema, setSchema] = useState([]);
    const [schemaValidation, setSchemaValidation] = useState([]);
    const [steps, setSteps] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [programData, setProgramsData] = useState([]);
    const [activeLeadbuyUniversity, setActiveLeadbuyUniversity] = useState({});
    const [formData, setFormData] = useState({});
    const [selectedProgramData, setSelectedProgramData] = useState({});
    const [offersLoading, setOffersLoading] = useState(true);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [isFormSubmitSuccess, setIsFormSubmitSuccess] = useState(false);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [locationDetail, setLocationDetail] = useState({});
    const [activeFormUni, setActiveFormUni] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const [degreeList, setDegreeList] = useState([]);
    const [categoryList, setCategoryList] = useState([]);
    const [categoryUrl, setCategoryUrl] = useState("");
    const [degreeUrl, setDegreeUrl] = useState("");
    const [responseLoading, setResponseLoading] = useState(false);
    const [degreeSelected, setDegreeSelected] = useState({});
    const [categorySelected, setCategorySelected] = useState(categoryUrl);
    const [pageCls, setPageCls] = useState(true);
    const visitTrx = useRef("");
    const [pageContent, setPageContent] = useState({});
    const availableVariants = ["a", "b", "c", "d"];
    const [variant, setVariant] = useState("");
    const { isLarge } = useWindowSize();
    const payload = {};
    const navigate = useNavigate();
    const params = useParams();
    const [submissionConstants, setSubmissionConstants] = useState();
    const [apiError, setApiError] = useState(false);

    useEffect(() => {
        const { category = "all", degree = "all" } = params;

        let categoryValue = getCategoryValue(category);
        let degreeValue = getDegreeValue(degree);
        // IF degreeValue and categoryValue is undefined then it is invalid path, redirect to degree-programs.
        if (categoryValue === undefined || degreeValue === undefined) {
            navigate("/degree-programs");
            setCategoryUrl("all");
            setDegreeUrl("all");
            return;
        }

        // Creating a payload which we will pass in getForm API.
        if (categoryValue !== "All") {
            let categoryId = CATEGORY_LIST.find(cat => cat.value === categoryValue)?.id;
            payload.category = [categoryId];
        }

        if (degreeValue !== "All") {
            payload["intended_degree_level"] = degreeValue;
        }

        setCategoryUrl(category);
        setDegreeUrl(degree);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const degreeValue = getDegreeValue(degreeUrl);
        const categoryValue = getCategoryValue(categoryUrl);

        const modifiedCategory = getCategoryList(degreeValue);
        const modifiedDegree = getDegreeList(categoryValue);
        
        const fetchCategoryMappingData = async () => {
            const {default: data} = await import(`../mappingdata/${categoryValue}`);
            return data;
        }

        const processCategoryMappingData = async () => {
            const data = await fetchCategoryMappingData();

            //Get Dynamic content for the specific url.
            let modifiedPageContent = data[degreeValue];
            if (Object.keys(modifiedPageContent).length === 0) {
                modifiedPageContent = data["All"];
            }
    
            // Change meta description for different url.
            modifyMetaDescription(modifiedPageContent?.meta_content);
    
            setPageContent(modifiedPageContent);
            setDegreeList(modifiedDegree);
            setCategoryList(modifiedCategory);
        }
        processCategoryMappingData();
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [degreeUrl, categoryUrl]);
    

    useEffect(() => {
        const fetchData = async () => {
            try {
                const visitData = await fetchVisitData(searchParams);
                visitData && trackGtmPixel('GL-V1-Page-Land');
                visitTrx.current = visitData._trx;
                const { initData, formConfigData } = await fetchInitForm(visitData._trx, payload, searchParams);
                if (initData && formConfigData) {
                    const firstKey = Object.keys(initData.location)[0];
                    const { city, stateCode, countryCode, postalCode } = initData.location[firstKey];
                    setLocationDetail({
                        city: capitalizeFirstLetter(city),
                        state: getStateFromCode(stateCode),
                        country: countryCode,
                        postalcode: postalCode,
                    });

                    const selectedDegree = params?.degree || "all";
                    const selectedCategory = params?.category || "all";

                    
                    let updatedMergedOffers = getMergedRankedOffers(formConfigData,selectedDegree,selectedCategory);
                    // If category is nursing and then hide regent
                    if(params?.category === "nursing"){
                        updatedMergedOffers = updatedMergedOffers?.filter(univ => univ?.college_id !== "regent-university");
                    }
                    if(params?.degree === "certificate-diploma" || params.degree === "associate" || params.degree === "bachelors"){
                        updatedMergedOffers = updatedMergedOffers?.filter(univ => univ?.college_id !== "national-university");
                    }
                    setMainData({
                        ...formConfigData,
                        mergedOffers: updatedMergedOffers
                    })
                    setSubmissionConstants(prev => ({
                        ...prev,
                        "fsid": initData.fsid
                    }));
                }
            } catch (error) {
                console.error(error);
                setApiError(true)
            }

        };
        fetchData();
        document.title = "Online University Degree Programs - CollegEnroll"
        let currentVariant = availableVariants.includes(searchParams.get("tmpl")) ? searchParams.get("tmpl") : "a" || "a";
        setVariant(currentVariant);
        searchParams.set("tmpl", currentVariant);
        setSearchParams(searchParams.toString());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // If response is valid then hiding Shimmer.
        if (mainData?.mergedOffers?.length > 0) {
            setOffersLoading(false);
            setResponseLoading(false);
            setTimeout(() => {
                setPageCls(false);
            }, 1000);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainData])

    

    const onCloseHandler = () => {
        setIsModalOpen(false);
    };

    const appContextValues = {
        locationDetail,
        setApiError,
        setLocationDetail,
        setMainData,
        mainData,
        degreeList,
        categoryList,
        categoryUrl,
        setCategoryUrl,
        degreeUrl,
        setDegreeUrl,
        setIsModalOpen,
        offersLoading,
        pageContent,
        variant,
        degreeSelected,
        setDegreeSelected,
        categorySelected,
        setCategorySelected
    };

    const FormContextValues = {
        setSubmissionConstants,
        submissionConstants,
        formData,
        setFormData,
        activeLeadbuyUniversity,
        programData,
        schema,
        setSchema,
        schemaValidation,
        setSchemaValidation,
        steps,
        selectedProgramData,
        setSelectedProgramData,
        isFormSubmitted,
        setIsFormSubmitted,
        isFormSubmitSuccess,
        setIsFormSubmitSuccess,
        isFormSubmitting,
        setIsFormSubmitting,
        activeFormUni,
        setSteps,
        setProgramsData,
        setActiveLeadbuyUniversity,
        setActiveFormUni
    };

    //Error Handling 
    if (apiError) {
        return (
            <div className={`min-h-full flex flex-col ${pageCls ? "page-cls" : ""}`}>
                <div className="flex-1">
                    <div className="main-wrapper">
                        <HeaderMessage />
                        <Header />
                        <Error />
                    </div>
                </div>
                <Footer />
            </div>
        )
    }

    return (
        <AppContext.Provider value={appContextValues}>
            <div className={`min-h-full flex flex-col ${pageCls ? "page-cls" : ""}`}>
                <div className="flex-1">
                    <div className="main-wrapper">
                        <HeaderMessage />
                        <Header />
                        <Banner />

                        <div className="flex justify-center lg:justify-between max-w-[1042px] px-4  pt-0 md:pt-8 mx-auto">
                            <div className="max-w-[730px] w-full lg:mr-[30px]">

                                {
                                    offersLoading ? (
                                        <>
                                            <FilterSkeleton />
                                            <CardSkeleton className="pt-[16px] md:pt-8" />
                                        </>
                                    ) : (
                                        <Suspense fallback={<>
                                            <FilterSkeleton />
                                            <CardSkeleton className="pt-[16px] md:pt-8" />
                                        </>}>
                                            <FormContext.Provider value={FormContextValues}>
                                                <SerpContainerLazy trx={visitTrx.current} responseLoading={responseLoading} setResponseLoading={setResponseLoading} />
                                            </FormContext.Provider>

                                            <div className="page-cls-1">
                                                <CollegeComparisonGuide />
                                                <FormContext.Provider value={FormContextValues}>
                                                    <OurTopUniversity />
                                                </FormContext.Provider>
                                                <FaqSection />
                                            </div>
                                        </Suspense>
                                    )
                                }

                            </div>
                            {
                                isLarge &&
                                <div className="max-w-[250px] w-full mt-[75px]">
                                    {
                                        offersLoading ? (
                                            <SideBarSkeleton />
                                        ) : (
                                            responseLoading ? (
                                                <SideBarSkeleton />
                                            ) : (
                                                <Suspense fallback={<SideBarSkeleton />}>
                                                    <CollegEnrollUSP />
                                                    <UniversityReviews />
                                                    <Article />
                                                </Suspense>
                                            )
                                        )
                                    }
                                </div>
                            }

                        </div>

                        {isModalOpen && (
                            <Modal onClose={onCloseHandler} modalClass="max-w-[582px]" modalBodyClass="rounded-2xl">
                                <FormContext.Provider value={FormContextValues}>
                                    <div className="md:max-h-[756px] bg-light-500 rounded-t-2xl rounded-b-lg md:rounded-b-2xl">
                                        <Suspense fallback={<FormSkeleton />}>
                                            {isFormSubmitting ? (
                                                <Loader />
                                            ) : isFormSubmitted ? (
                                                <Results
                                                    formData={formData}
                                                    isFormSubmitSuccess={isFormSubmitSuccess}
                                                />
                                            ) : (
                                                <Form />
                                            )}
                                        </Suspense>
                                    </div>
                                </FormContext.Provider>
                            </Modal>
                        )}
                    </div>
                </div>
                <div className="page-cls-1">
                    <Footer />
                </div>
            </div>
            <div id="modal"></div>
        </AppContext.Provider>
    );
};

export default Home;

export const AppContext = createContext({
    setApiError: () => { },
    locationDetail: {},
    setLocationDetail: {},
    setMainData: {},
    mainData: {},
    degreeList: [],
    categoryList: [],
    categoryUrl: String,
    setCategoryUrl: () => { },
    degreeUrl: String,
    setDegreeUrl: () => { },
    setIsModalOpen: () => { },
    offersLoading: true,
    pageContent: {},
    variant: String,
    degreeSelected: {},
    setDegreeSelected: () => { },
    categorySelected: {},
    setCategorySelected: () => { }
});

export const FormContext = createContext({
    submissionConstants: {},
    setSubmissionConstants: {},
    activeLeadbuyUniversity: {},
    programData: [],
    schema: [],
    setSchema: () => { },
    schemaValidation: [],
    setSchemaValidation: () => { },
    steps: [],
    formData: {},
    setFormData: () => { },
    selectedProgramData: {},
    setSelectedProgramData: () => { },
    isFormSubmitted: false,
    setIsFormSubmitted: () => { },
    isFormSubmitSuccess: false,
    setIsFormSubmitSuccess: () => { },
    isFormSubmitting: false,
    setIsFormSubmitting: () => { },
    activeFormUni: String,
    setSteps: () => { },
    setProgramsData: () => { },
    setActiveLeadbuyUniversity: () => { },
    setActiveFormUni: () => { }
});